import { Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { mergeMap, of, withLatestFrom } from 'rxjs';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

import {
    CancelInstance,
    CancelProcessInstance,
    ClearCustomEntityRecordsPagination,
    ClearDownloadableFiles,
    ClearInstances,
    ClearProcessInstances,
    ClearRecordsPagination,
    CreateBulkReportsDownloadRequest,
    CreateServiceBulkReportsDownloadRequest,
    DeleteCustomEntityRecords,
    DeleteRecords,
    GenerateInstanceReport,
    GenerateReport,
    GetAllMapTransformations,
    GetAllTransformations,
    GetAppMetaData,
    GetAppMetaDataPermissions,
    GetAuditChangedEntities,
    GetCollaborators,
    GetCustomAppMetaData,
    GetCustomAppMetaDataPermissions,
    GetCustomAppPermissions,
    GetCustomAppWorkflow,
    GetCustomAuditChangedEntities,
    GetCustomEntityRecordChangeLogs,
    GetDownloadableFiles,
    GetEventTypes,
    GetInstanceCollaborators,
    GetInstanceDownloadableFiles,
    GetInstanceEventTypes,
    GetInstanceIntegrationStatus,
    GetInstancePreCapturedAttributes,
    GetInstanceReports,
    GetInstances,
    GetInstanceTransformations,
    GetIntegrationStatus,
    GetPermissionsByTypes,
    GetPreCapturedAttributes,
    GetProcessInstances,
    GetRecordChangeLogs,
    GetReports,
    GetServiceTransformations,
    GetWorkflow,
    RetryInstanceIntegrationStatus,
    RetryIntegrationStatus,
    SaveOrUpdateCustomEntityRecord,
    SaveOrUpdateEntityRecord,
    SetCustomAppServiceId,
    SetCustomAppsRestApiName,
    SetCustomEntityRecordsPagination,
    SetCustomEntityRecordsSearchCriteria,
    SetDownloadableFiles,
    SetInstancesPagination,
    SetProcessInstancesPagination,
    SetRecordsPagination,
    SetRecordsSearchCriteria,
    SetSelectedAppRestApiName,
    SetSelectedAppServiceId,
    UpdateCustomEntityRecords,
    UpdateEntityRecords,
} from '../actions';
import {
    getCustomAppDetails$,
    getCustomAppServiceId$,
    getCustomAppsRestApiName$,
    getCustomInstanceId$,
    getCustomInstances$,
    getCustomOrganizationId$,
    getCustomSelectedEntityParentRecordId$,
    getCustomSelectedEntityUId$,
    getCustomSessionUserPermissions$,
    getEninvoiceSelectedAppRestApiName$,
    getIntegrationStatus$,
    getSelectedAppServiceId$,
} from '../selectors';

@Injectable()
export class CustomEffects {
    constructor(private actions$: ActionsSubject, private store$: Store) {}

    updateInstancesPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetProcessInstancesPagination),
            withLatestFrom(this.store$.select(getCustomOrganizationId$), this.store$.select(getSelectedAppServiceId$)),
            mergeMap(([action, organizationId, serviceId]) =>
                of(
                    SetInstancesPagination({
                        organizationId: organizationId,
                        serviceId: serviceId,
                        size: action.pagination.size,
                        pageIndex: action.pagination.pageIndex,
                    })
                )
            )
        )
    );

    getInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetProcessInstances),
            withLatestFrom(this.store$.select(getCustomInstances$)),
            mergeMap(([action, instances]) => {
                if (instances?.length > 0) {
                    return of();
                }
                return of(
                    GetInstances({
                        noAlerts: action.noAlerts,
                    })
                );
            })
        )
    );

    clearInstances = createEffect(() =>
        this.actions$.pipe(
            ofType(ClearProcessInstances),
            withLatestFrom(this.store$.select(getCustomOrganizationId$), this.store$.select(getSelectedAppServiceId$)),
            mergeMap(([_action, organizationId, serviceId]) =>
                of(
                    ClearInstances({
                        organizationId: organizationId,
                        serviceId: serviceId,
                    })
                )
            )
        )
    );

    cancelProcessInstance$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CancelProcessInstance),
            withLatestFrom(
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getEninvoiceSelectedAppRestApiName$)
            ),
            mergeMap(([_action, restAPIName, instanceId, eninvoiceRestApiName]) =>
                of(
                    CancelInstance({
                        instanceId: _action.instanceId || instanceId,
                        restAPIName: _action.restApiName || restAPIName || eninvoiceRestApiName,
                        comment: _action.comment,
                    })
                )
            )
        )
    );

    getPreCapturedAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetPreCapturedAttributes),
            withLatestFrom(this.store$.select(getCustomAppsRestApiName$), this.store$.select(getCustomInstanceId$)),
            mergeMap(([_action, restAPIName, instanceId]) =>
                of(GetInstancePreCapturedAttributes({ restAPIName, instanceId: instanceId || _action.instanceId, noAlerts: true }))
            )
        )
    );

    getIntegrationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetIntegrationStatus),
            withLatestFrom(
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getIntegrationStatus$)
            ),
            mergeMap(([_action, restAPIName, instanceId, integrationStatuses]) =>
                integrationStatuses?.[instanceId]
                    ? of()
                    : of(
                          GetInstanceIntegrationStatus({
                              restAPIName,
                              instanceId: _action.instanceId || instanceId,
                              noAlerts: _action.noAlerts,
                          })
                      )
            )
        )
    );

    retryIntegrationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RetryIntegrationStatus),
            withLatestFrom(this.store$.select(getCustomInstanceId$), this.store$.select(getIntegrationStatus$)),
            mergeMap(([_action, instanceId, integrationStatuses]) => {
                const status = integrationStatuses?.[instanceId];
                if (status && status !== 'FAILED') {
                    return of();
                }
                return of(
                    RetryInstanceIntegrationStatus({
                        requestId: _action.requestId,
                        unitId: _action.unitId,
                        assetId: _action.assetId,
                        bridgeId: _action.bridgeId,
                        serviceId: _action.serviceId,
                        assetDataId: _action.assetDataId,
                        userId: _action.userId,
                        requestLevel: _action.requestLevel,
                        noAlerts: _action.noAlerts,
                    })
                );
            })
        )
    );

    getReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInstanceReports),
            withLatestFrom(this.store$.select(getCustomInstanceId$), this.store$.select(getCustomAppDetails$)),
            mergeMap(([_action, instanceId, appDetails]) =>
                of(
                    GetReports({
                        instanceId: _action.instanceId || instanceId,
                        serviceId: appDetails?.serviceId,
                        assetId: appDetails?.assetMetaUId,
                        noAlerts: _action.noAlerts,
                        forceFetch: _action.forceFetch,
                    })
                )
            )
        )
    );

    generateReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GenerateInstanceReport),
            withLatestFrom(
                this.store$.select(getCustomOrganizationId$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomAppDetails$)
            ),
            mergeMap(([_action, organizationId, instanceId, appDetails]) =>
                of(
                    GenerateReport({
                        instanceId: _action.instanceId || instanceId,
                        serviceId: appDetails?.serviceId,
                        repositoryId: organizationId,
                        chainName: _action.chainName,
                        noAlerts: _action.noAlerts,
                        timeStamp: CommonUtilsService.getOrgAndUserOffsetTimeStamp(),
                    })
                )
            )
        )
    );

    getInstanceEventTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInstanceEventTypes),
            mergeMap(() => of(GetEventTypes({ forSubscription: false })))
        )
    );

    getAllMapTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInstanceTransformations),
            withLatestFrom(this.store$.select(getCustomInstanceId$), this.store$.select(getCustomAppDetails$)),
            mergeMap(([_action, instanceId, appDetails]) =>
                of(GetAllMapTransformations({ instanceId: _action.instanceId || instanceId, assetUid: appDetails?.assetMetaUId }))
            )
        )
    );

    getDownloadableFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInstanceDownloadableFiles),
            withLatestFrom(this.store$.select(getCustomInstanceId$), this.store$.select(getCustomAppDetails$)),
            mergeMap(([_action, instanceId, appDetails]) =>
                of(GetDownloadableFiles({ instanceId: _action.instanceId || instanceId, restAPIName: appDetails?.restApiName }))
            )
        )
    );

    clearDownloadableFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ClearDownloadableFiles),
            withLatestFrom(this.store$.select(getCustomInstanceId$)),
            mergeMap(([action, instanceId]) => of(SetDownloadableFiles({ instanceId: action.instanceId || instanceId, files: [] })))
        )
    );

    getInstanceCollaborators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInstanceCollaborators),
            withLatestFrom(this.store$.select(getCustomInstanceId$)),
            mergeMap(([action, instanceId]) =>
                of(GetCollaborators({ instanceId: action.instanceId || instanceId, assetId: action.assetId, noAlerts: action.noAlerts }))
            )
        )
    );

    getServiceTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetServiceTransformations),
            mergeMap(action =>
                of(
                    GetAllTransformations({
                        serviceId: action.serviceId,
                        restApiName: action.restApiName,
                        noAlerts: action.noAlerts,
                        forceFetch: action.forceFetch,
                    })
                )
            )
        )
    );

    createServiceBulkReportsDownloadRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateServiceBulkReportsDownloadRequest),
            withLatestFrom(this.store$.select(getCustomAppDetails$)),
            mergeMap(([action, appDetails]) =>
                of(
                    CreateBulkReportsDownloadRequest({
                        serviceId: action.serviceId || appDetails?.serviceId,
                        transformationNames: action.transformationNames,
                        instanceIds: action.instanceIds,
                    })
                )
            )
        )
    );

    setCustomAppServiceId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetCustomAppServiceId),
            mergeMap(action => of(SetSelectedAppServiceId({ serviceId: action.appServiceId })))
        )
    );

    setCustomAppRestApiName$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetCustomAppsRestApiName),
            mergeMap(action => of(SetSelectedAppRestApiName({ restApiName: action.appRestApiName })))
        )
    );

    getCustomAppMetaData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomAppMetaData),
            withLatestFrom(this.store$.select(getCustomAppServiceId$)),
            mergeMap(([_action, serviceId]) => of(GetAppMetaData({ serviceId: serviceId })))
        )
    );

    getCustomAppMetaDataPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomAppMetaDataPermissions),
            withLatestFrom(this.store$.select(getCustomAppServiceId$)),
            mergeMap(([_action, serviceId]) => of(GetAppMetaDataPermissions({ serviceId: serviceId, assetType: 'ASSET' })))
        )
    );

    getCustomAppPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomAppPermissions),
            withLatestFrom(this.store$.select(getCustomAppServiceId$), this.store$.select(getCustomSessionUserPermissions$)),
            mergeMap(([_action, serviceId, permissions]) => {
                const appPermissions = permissions?.['ASSET']?.[serviceId];
                if (appPermissions) return [];
                return of(GetPermissionsByTypes({ permissionTypes: ['ASSET'], categories: [serviceId] }));
            })
        )
    );

    getCustomAuditChangedEntities$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomAuditChangedEntities),
            withLatestFrom(this.store$.select(getCustomInstanceId$)),
            mergeMap(([_action, instanceId]) =>
                of(
                    GetAuditChangedEntities({
                        instanceId: instanceId,
                        parentRecordId: _action.parentRecordId,
                        timeStamp: _action.timeStamp,
                    })
                )
            )
        )
    );

    clearCustomEntityRecordsPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ClearCustomEntityRecordsPagination),
            withLatestFrom(
                this.store$.select(getCustomOrganizationId$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomAppServiceId$),
                this.store$.select(getCustomSelectedEntityUId$),
                this.store$.select(getCustomSelectedEntityParentRecordId$)
            ),
            mergeMap(([_action, organizationId, instanceId, serviceId, entityUId, parentRecordId]) =>
                of(
                    ClearRecordsPagination({
                        organizationId: organizationId,
                        serviceId: serviceId,
                        instanceId: instanceId,
                        entityUid: entityUId,
                        parentRecordId: parentRecordId,
                    })
                )
            )
        )
    );

    setCustomEntityRecordsPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetCustomEntityRecordsPagination),
            withLatestFrom(
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomAppServiceId$),
                this.store$.select(getCustomSelectedEntityUId$),
                this.store$.select(getCustomSelectedEntityParentRecordId$)
            ),
            mergeMap(([_action, instanceId, serviceId, entityUId, parentRecordId]) =>
                of(
                    SetRecordsPagination({
                        serviceId: serviceId || _action.serviceId,
                        instanceId: instanceId || _action.instanceId,
                        entityUid: entityUId || _action.entityUId,
                        parentRecordId: parentRecordId || _action.parentRecordId,
                        pageSize: _action.size,
                        pageIndex: _action.pageIndex,
                        cursor: _action.pagingState,
                        noAlerts: _action.noAlerts,
                    })
                )
            )
        )
    );

    setCustomEntityRecordsSearchCriteria$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetCustomEntityRecordsSearchCriteria),
            withLatestFrom(
                this.store$.select(getCustomOrganizationId$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomAppServiceId$),
                this.store$.select(getCustomSelectedEntityUId$),
                this.store$.select(getCustomSelectedEntityParentRecordId$)
            ),
            mergeMap(([_action, organizationId, instanceId, serviceId, entityUId, parentRecordId]) =>
                of(
                    SetRecordsSearchCriteria({
                        organizationId: organizationId || _action.organizationId,
                        serviceId: serviceId || _action.serviceId,
                        instanceId: instanceId || _action.instanceId,
                        entityUid: entityUId || _action.entityUId,
                        parentRecordId: parentRecordId || _action.parentRecordId,
                        criteria: _action.searchCriteria,
                    })
                )
            )
        )
    );

    getCustomAppWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomAppWorkflow),
            withLatestFrom(
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getCustomAppServiceId$)
            ),
            mergeMap(([_action, instanceId, restApiName, serviceId]) =>
                of(GetWorkflow({ instanceId: instanceId, restApiName: restApiName, serviceId: serviceId, noAlerts: false }))
            )
        )
    );

    saveOrUpdateCustomEntityRecord$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SaveOrUpdateCustomEntityRecord),
            withLatestFrom(
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getCustomAppServiceId$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomSelectedEntityUId$),
                this.store$.select(getCustomSelectedEntityParentRecordId$)
            ),
            mergeMap(([_action, restApiName, serviceId, instanceId, entityUId, parentRecordId]) => {
                return of(
                    SaveOrUpdateEntityRecord({
                        serviceId: serviceId || _action.serviceId,
                        restAPIName: restApiName || _action.restAPIName,
                        instanceId: instanceId || _action.instanceId,
                        parentRecordId: parentRecordId || _action.parentRecordId,
                        entityUId: entityUId || _action.entityUId,
                        entities: _action.entities,
                        record: _action.record,
                        isArray: _action.isArray,
                        configurations: _action.configurations,
                        process: _action.process,
                        noAlerts: _action.noAlerts,
                    })
                );
            })
        )
    );

    updateCustomEntityRecords$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCustomEntityRecords),
            withLatestFrom(
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getCustomAppServiceId$),
                this.store$.select(getCustomInstanceId$),
                this.store$.select(getCustomSelectedEntityUId$),
                this.store$.select(getCustomSelectedEntityParentRecordId$)
            ),
            mergeMap(([_action, restApiName, serviceId, instanceId, entityUId, parentRecordId]) => {
                return of(
                    UpdateEntityRecords({
                        serviceId: serviceId || _action.serviceId,
                        restAPIName: restApiName || _action.restAPIName,
                        instanceId: instanceId || _action.instanceId,
                        parentRecordId: parentRecordId || _action.parentRecordId,
                        entityUId: entityUId || _action.entityUId,
                        payload: _action.recordsPayload,
                        noAlerts: _action.noAlerts,
                        noMessages: _action.noMessages,
                    })
                );
            })
        )
    );

    deleteCustomEntityRecords$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCustomEntityRecords),
            mergeMap(action => {
                return of(
                    DeleteRecords({
                        serviceId: action.serviceId,
                        instanceId: action.instanceId,
                        parentRecordId: action.parentRecordId,
                        entityId: action.entityId,
                        payload: action.payload,
                        noAlerts: action.noAlerts,
                        noMessages: action.noMessages,
                    })
                );
            })
        )
    );

    getCustomEntityRecordChangeLogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetCustomEntityRecordChangeLogs),
            mergeMap(action => {
                return of(
                    GetRecordChangeLogs({
                        assetId: action.assetId,
                        instanceId: action.instanceId,
                        parentRecordId: action.parentRecordId,
                        entityId: action.entityUId,
                        recordId: action.recordId,
                    })
                );
            })
        )
    );
}
